import { z } from 'zod';

// =============================================================================
// Match weights (stage 1)
//
// The per-signal points the referral → appointment-type scorer awards. These
// were hardcoded as `SCORE_WEIGHTS` in the API's `scorer.ts`; the defaults below
// reproduce those constants exactly so making them configurable moves nothing.
//
// Keys are camelCase inside the JSONB blob, matching the existing convention
// for `appointment_types.constraints` (`businessHours`, `maxPerDay`, …).
// =============================================================================

export const matchWeightsSchema = z.object({
  /** Points per matching CPT code. "Most codes wins" falls out of this. */
  cptMatch: z.number().default(10),
  /** Points per matching ICD code. */
  icdMatch: z.number().default(8),
  /** Type is specifically for referred patients. */
  requiresReferral: z.number().default(5),
  /** Urgent referral landing on an urgency-flagged type. */
  urgencyMatch: z.number().default(5),
  /** Type's patient restriction matches the referral's patient type. */
  patientTypeMatch: z.number().default(3),
  /** Penalty applied to generic catch-all types. Negative by convention. */
  genericPenalty: z.number().default(-20),
});

export type MatchWeights = z.infer<typeof matchWeightsSchema>;

export const DEFAULT_MATCH_WEIGHTS: MatchWeights = matchWeightsSchema.parse({});

// =============================================================================
// Prioritization (stage 3)
// =============================================================================

/** Registered priority strategies. Adding one means one entry here + one in the API registry. */
export const PRIORITY_STRATEGY_KEYS = ['fairness', 'providerWeight'] as const;

export const priorityStrategyKeySchema = z.enum(PRIORITY_STRATEGY_KEYS);

export type PriorityStrategyKey = z.infer<typeof priorityStrategyKeySchema>;

export const priorityStrategyConfigSchema = z.object({
  key: priorityStrategyKeySchema,
  /**
   * Relative influence within a tier; must sum to 1 across all strategies.
   *
   * This is the BLEND weight of a strategy — distinct from the per-provider
   * weight the `providerWeight` strategy reads. The strategy is named in full
   * so the pair stays readable: `{ key: 'providerWeight', weight: 0.4 }`.
   */
  weight: z.number().min(0).max(1),
});

export type PriorityStrategyConfig = z.infer<typeof priorityStrategyConfigSchema>;

/**
 * Where a residual match-score difference breaks a tie, relative to the
 * composite priority score.
 *   - `after`  (default) — fairness/rating decide first; match score only
 *     separates providers the composite left equal. Weak signals like the
 *     +3 patient-type bonus should not outrank a large load imbalance.
 *   - `before` — match score dominates; priority only breaks exact ties.
 *   - `off`    — match score is not consulted past the tier.
 */
export const matchScoreTieBreakSchema = z.enum(['before', 'after', 'off']);

export type MatchScoreTieBreak = z.infer<typeof matchScoreTieBreakSchema>;

const WEIGHT_SUM_EPSILON = 1e-6;

/** True when the strategy weights sum to 1 (within float tolerance). */
export function weightsSumToOne(strategies: PriorityStrategyConfig[]): boolean {
  if (strategies.length === 0) return true;
  const total = strategies.reduce((sum, s) => sum + s.weight, 0);
  return Math.abs(total - 1) < WEIGHT_SUM_EPSILON;
}

export const prioritizationSchema = z
  .object({
    strategies: z
      .array(priorityStrategyConfigSchema)
      .default([
        { key: 'fairness', weight: 0.6 },
        { key: 'providerWeight', weight: 0.4 },
      ])
      // A duplicated key would silently double-count that strategy's weight.
      .refine((s) => new Set(s.map((x) => x.key)).size === s.length, {
        message: 'Each strategy may appear at most once',
      }),
    matchScoreTieBreak: matchScoreTieBreakSchema.default('after'),
    /** Final deterministic tie-break so repeated runs return a stable order. */
    tieBreak: z.literal('provider_id').default('provider_id'),
  })
  // Enforced at save time so a misconfigured org fails loudly at the settings
  // form rather than silently skewing every referral's routing.
  .refine((p) => weightsSumToOne(p.strategies), {
    message: 'Strategy weights must sum to 1',
    path: ['strategies'],
  });

export type Prioritization = z.infer<typeof prioritizationSchema>;

export const DEFAULT_PRIORITIZATION: Prioritization = prioritizationSchema.parse({});

// =============================================================================
// Per-run overrides
//
// Sent by the simulator so staff can tune the algorithm and watch the order
// move WITHOUT persisting anything. Never written to the settings row — the
// service merges them over the org's saved settings for that request only.
// =============================================================================

export const selectionOverridesSchema = z.object({
  matchWeights: matchWeightsSchema.partial().optional(),
  prioritization: z
    .object({
      // Same two rules the saved config enforces. Without them a run could be
      // simulated with weights summing to 2, or with a strategy listed twice
      // (double-counted in the composite) — and the identical values would then
      // be REJECTED by `Save as org default`, so a working preview could not be
      // persisted.
      strategies: z
        .array(priorityStrategyConfigSchema)
        .refine((s) => new Set(s.map((x) => x.key)).size === s.length, {
          message: 'Each strategy may appear at most once',
        })
        .refine(weightsSumToOne, { message: 'Strategy weights must sum to 1' })
        .optional(),
      matchScoreTieBreak: matchScoreTieBreakSchema.optional(),
    })
    .optional(),
});

export type SelectionOverrides = z.infer<typeof selectionOverridesSchema>;

// =============================================================================
// Qualifying match (stage 2 gate)
//
// Which referral signals make a provider a genuine MATCH — as opposed to a
// relaxed fallback the scorer let back in. This is the "match" in the
// match → rank → default pipeline: the priority weights only ever reorder
// providers that qualify here, and a referral that qualifies NOBODY routes to
// `defaultProviderId` instead of being offered a specialist it never matched.
//
// A provider qualifies when ANY of its eligible rows matched at least one code
// of a listed signal type (not only its top-scoring row — a provider whose best
// type matches a non-qualifying signal but who also offers a qualifying-matching
// type is still a match). `['CPT','ICD']` (the default) reproduces the historical
// behavior — any code overlap counts. `['ICD']` requires a diagnosis match, so
// a CPT-only referral (e.g. a sleep study authorized without a diagnosis) no
// longer maps to whichever specialist happens to perform that procedure.
//
// Codes only: the referral/urgency/patient-type bonuses are ranking weight, not
// qualification — a provider that matched no clinical code is not a match.
// =============================================================================

export const QUALIFYING_SIGNALS = ['CPT', 'ICD'] as const;

export const qualifyingSignalSchema = z.enum(QUALIFYING_SIGNALS);

export type QualifyingSignal = z.infer<typeof qualifyingSignalSchema>;

/**
 * The signals that qualify a provider as a match. An empty list means nothing
 * qualifies — every referral routes to the default provider — which is a valid
 * (if drastic) deliberate configuration.
 *
 * NO `.default()` here on purpose: the update schema wraps this in `.optional()`
 * for partial saves, and a `.default()` would fire on an absent key and silently
 * reset the field on every save that didn't include it. Defaulting is applied
 * only where a value is actually required — the settings response schema below.
 */
export const qualifyingSignalsSchema = z.array(qualifyingSignalSchema);

/** Both code types qualify — reproduces the historical any-code-overlap behavior. */
export const DEFAULT_QUALIFYING_SIGNALS: QualifyingSignal[] = ['CPT', 'ICD'];

// =============================================================================
// Settings
// =============================================================================

export const WIDEN_MODES = ['widen', 'switch'] as const;

export const widenModeSchema = z.enum(WIDEN_MODES);

export type WidenMode = z.infer<typeof widenModeSchema>;

export const providerSelectionSettingsSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  matchWeights: matchWeightsSchema,
  /** Which signals qualify a provider as a match (match → rank → default). */
  // Factory default (not the bare constant): Zod hands a non-function default
  // out by reference, which would alias the shared `DEFAULT_QUALIFYING_SIGNALS`
  // array across every parsed settings object.
  qualifyingSignals: qualifyingSignalsSchema.default(() => [...DEFAULT_QUALIFYING_SIGNALS]),
  /** Fallback provider when nothing matches at all. Null = no fallback configured. */
  defaultProviderId: z.string().nullable(),
  prioritization: prioritizationSchema,
  maxPoolSize: z.number().int().min(1),
  // Stage 4 (in-call widening) — stored now, unread until that ships.
  offersBeforeWidening: z.number().int().min(1),
  widenMode: widenModeSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ProviderSelectionSettings = z.infer<typeof providerSelectionSettingsSchema>;

export const updateProviderSelectionSettingsSchema = z.object({
  matchWeights: matchWeightsSchema.optional(),
  qualifyingSignals: qualifyingSignalsSchema.optional(),
  defaultProviderId: z.string().uuid().nullable().optional(),
  prioritization: prioritizationSchema.optional(),
  maxPoolSize: z.number().int().min(1).max(20).optional(),
  offersBeforeWidening: z.number().int().min(1).max(10).optional(),
  widenMode: widenModeSchema.optional(),
});

export type UpdateProviderSelectionSettingsRequest = z.infer<
  typeof updateProviderSelectionSettingsSchema
>;
